import { Vector3 } from 'three';
import { AppStore } from './store';
import { TrackNode, makeNode, splitSpan, dirtySpansForNode, spanCount } from '../core/track/spline';
import { templates, TemplateId, EntryFrame } from '../core/track/templates';
import { Centerline } from '../core/track/centerline';
import {
  RctPieceId,
  RCT_PIECE_BY_ID,
  rctEntryFrom,
  snapToGrid,
  snapHeight,
  snapHeading,
} from '../core/track/rctPieces';
import { smoothHandles } from '../core/track/smooth';
import { autoSolveTrackWarnings } from '../core/track/autoSolve';
import { TrackWarning, validateTrack } from '../core/track/validate';
import { autoBankKeys, rollAt, rollKeysFromNodes, RollKey, sortRollKeys } from '../core/track/roll';
import { gravityBankDefinable, tanFromYawPitch } from '../core/track/orientation';
import { convertNodeAuthoredBanksToGravity } from '../core/track/flyBank';
import { Section } from '../core/track/sections';
import { Simulator } from '../core/sim/physics';

/** High-level editing operations. Each is one undo step. */

export function appendNode(store: AppStore, p: Vector3): void {
  store.commit((doc) => {
    const nodes = doc.track.nodes;
    if (nodes.length === 0) {
      doc.start.position.copy(p);
      nodes.push(makeNode(p, new Vector3(-4, 0, 0), new Vector3(4, 0, 0)));
      return;
    }
    const last = nodes[nodes.length - 1];
    const dir = p.clone().sub(last.p);
    const dist = Math.max(dir.length(), 0.001);
    dir.normalize();
    // aim the previous node's out-handle toward the new node for a smooth join
    if (nodes.length >= 1) {
      const blend = last.hOut.clone().normalize().add(dir).normalize();
      if (blend.lengthSq() > 0.01) last.hOut.copy(blend.multiplyScalar(dist / 3));
    }
    nodes.push(makeNode(p, dir.clone().multiplyScalar(-dist / 3), dir.clone().multiplyScalar(dist / 3)));
  });
}

/**
 * Append a control point while flying: position + flight direction for handles,
 * and tip bank so scroll-twist banks the rails. Open tracks only.
 * Returns the new node id, or null if the track is closed / direction is zero.
 */
export function appendFlyNode(
  store: AppStore,
  p: Vector3,
  forward: Vector3,
  twistDeg: number,
): string | null {
  if (store.doc.track.closed) return null;
  const dir = forward.clone();
  if (dir.lengthSq() < 1e-8) return null;
  dir.normalize();

  let newId: string | null = null;
  store.commit((doc) => {
    const nodes = doc.track.nodes;
    if (nodes.length === 0) {
      doc.start.position.copy(p);
      const handleLen = 4;
      const node = makeNode(p, dir.clone().multiplyScalar(-handleLen), dir.clone().multiplyScalar(handleLen));
      if (gravityBankDefinable(dir)) node.forcedBankDeg = Math.round(twistDeg);
      nodes.push(node);
      newId = node.id;
      if (node.forcedBankDeg !== undefined) {
        convertNodeAuthoredBanksToGravity(new Centerline(doc.track), [0]);
      }
    } else {
      const last = nodes[nodes.length - 1];
      const join = p.clone().sub(last.p);
      const dist = Math.max(join.length(), 0.001);
      join.normalize();
      // aim previous out-handle along the chord for a smooth join
      last.hOut.copy(join).multiplyScalar(dist / 3);
      if (last.mode === 'smooth') {
        const inLen = last.hIn.length() || dist / 3;
        last.hIn.copy(last.hOut).normalize().multiplyScalar(-inLen);
      }
      const node = makeNode(p, dir.clone().multiplyScalar(-dist / 3), dir.clone().multiplyScalar(dist / 3));
      if (gravityBankDefinable(dir)) node.forcedBankDeg = Math.round(twistDeg);
      nodes.push(node);
      newId = node.id;
      if (node.forcedBankDeg !== undefined) {
        convertNodeAuthoredBanksToGravity(new Centerline(doc.track), [nodes.length - 1]);
      }
    }
  });
  return newId;
}

/** Drop the last control point (fly-mode Q). Returns false if nothing to remove. */
export function revertLastNode(store: AppStore): boolean {
  const nodes = store.doc.track.nodes;
  if (nodes.length === 0) return false;
  deleteNodes(store, new Set([nodes[nodes.length - 1].id]));
  return true;
}

export function deleteNodes(store: AppStore, ids: Set<string>): void {
  if (ids.size === 0) return;
  const nodes = store.doc.track.nodes;
  const deletedIdx = nodes.map((n, i) => (ids.has(n.id) ? i : -1)).filter((i) => i >= 0);
  if (deletedIdx.length === 0) return;

  // Select the node just before the earliest deleted index so holding Delete
  // walks backward along the track. No previous → clear selection.
  const minDeleted = Math.min(...deletedIdx);
  const prevId = minDeleted > 0 ? nodes[minDeleted - 1].id : null;

  const cl = store.runtime.centerline;
  const L = store.runtime.totalLength;
  const killRanges = arcRangesForDeletedNodes(deletedIdx, nodes.length, cl.spanStartS.bind(cl), L);

  store.commit((doc) => {
    doc.track.nodes = doc.track.nodes.filter((n) => !ids.has(n.id));
    if (doc.track.nodes.length < 3) doc.track.closed = false;

    if (doc.track.nodes.length === 0) {
      doc.rollKeys = [];
      doc.sections = [];
      doc.verticalInverts = [];
      return;
    }

    // Drop section data that lived on the deleted run. Banking lives on nodes,
    // so deleted nodes take their forcedBankDeg with them.
    doc.sections = pruneSections(doc.sections, killRanges);

    const newL = new Centerline(doc.track).totalLength;
    doc.sections = clampSectionsToLength(doc.sections, newL);
    doc.verticalInverts = [];
  });
  store.setSelection(prevId ? [prevId] : []);
}

/**
 * Move selection to the next (+1) or previous (−1) control point.
 * Multi-select steps from the farthest node in that direction; empty selection
 * starts at the first (next) or last (previous) node. Closed tracks wrap.
 */
export function selectAdjacentNode(store: AppStore, delta: 1 | -1): void {
  const nodes = store.doc.track.nodes;
  if (nodes.length === 0) return;

  const idxs = store.selectedIndices();
  let next: number;
  if (idxs.length === 0) {
    next = delta > 0 ? 0 : nodes.length - 1;
  } else {
    const from = delta > 0 ? Math.max(...idxs) : Math.min(...idxs);
    next = from + delta;
    if (store.doc.track.closed) {
      next = ((next % nodes.length) + nodes.length) % nodes.length;
    } else {
      next = Math.max(0, Math.min(nodes.length - 1, next));
    }
  }
  store.setSelection([nodes[next].id]);
}

/** Contiguous deleted node runs → arc-length ranges [s0, s1] on the old centerline. */
export function arcRangesForDeletedNodes(
  deletedIdx: number[],
  nodeCount: number,
  spanStartS: (i: number) => number,
  totalLength: number,
): { s0: number; s1: number }[] {
  const runs = contiguousRuns(deletedIdx);
  return runs.map(([lo, hi]) => {
    const s0 = spanStartS(lo);
    const s1 = hi + 1 < nodeCount ? spanStartS(hi + 1) : totalLength;
    return { s0: isFinite(s0) ? s0 : 0, s1: isFinite(s1) ? Math.max(s1, s0) : totalLength };
  });
}

function contiguousRuns(indices: number[]): [number, number][] {
  if (indices.length === 0) return [];
  const sorted = [...indices].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  let a = sorted[0];
  let b = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === b + 1) b = sorted[i];
    else {
      runs.push([a, b]);
      a = b = sorted[i];
    }
  }
  runs.push([a, b]);
  return runs;
}

function inAnyRange(s: number, ranges: { s0: number; s1: number }[]): boolean {
  return ranges.some((r) => s >= r.s0 - 1e-6 && s <= r.s1 + 1e-6);
}

export function pruneRollKeys(keys: RollKey[], killRanges: { s0: number; s1: number }[]): RollKey[] {
  if (killRanges.length === 0) return keys;
  return keys.filter((k) => !inAnyRange(k.s, killRanges));
}

export function pruneSections(sections: Section[], killRanges: { s0: number; s1: number }[]): Section[] {
  if (killRanges.length === 0) return sections;
  return sections.filter((sec) => {
    // drop if fully inside or overlapping a deleted run
    return !killRanges.some((r) => sec.from < r.s1 - 1e-6 && sec.to > r.s0 + 1e-6);
  });
}

function clampSectionsToLength(sections: Section[], newL: number): Section[] {
  const out: Section[] = [];
  for (const sec of sections) {
    if (sec.from > newL + 1e-6) continue;
    const to = Math.min(sec.to, newL);
    if (to - sec.from < 1e-6) continue;
    out.push({ ...sec, to });
  }
  return out;
}

export function insertNodeOnCurve(store: AppStore, span: number, u: number): string | null {
  let newId: string | null = null;
  store.commit((doc) => {
    const res = splitSpan(doc.track, span, u);
    const nNodes = doc.track.nodes.length;
    const aIdx = span % nNodes;
    const bIdx = (span + 1) % nNodes;
    doc.track.nodes[aIdx].hOut.copy(res.hOutA);
    doc.track.nodes[bIdx].hIn.copy(res.hInB);
    doc.track.nodes.splice(span + 1, 0, res.newNode);
    newId = res.newNode.id;
  });
  return newId;
}

/**
 * Insert a template at the end of the (open) track, or fail on closed tracks.
 * Empty tracks get a first piece from the document start pose.
 * Selects the newly appended nodes so Insert / Delete can walk the tip.
 */
export function appendTemplate(store: AppStore, id: TemplateId, params?: Record<string, number>): boolean {
  const doc = store.doc;
  if (doc.track.closed) return false;

  const nodes = doc.track.nodes;
  let entry: EntryFrame;
  if (nodes.length === 0) {
    const pos = doc.start.position.clone();
    if (pos.y < 1) pos.y = 5;
    const tan = tanFromYawPitch(doc.start.heading, 0);
    if (tan.lengthSq() < 1e-8) tan.set(1, 0, 0);
    entry = { pos, tan, up: new Vector3(0, 1, 0) };
  } else {
    const endFrame = store.runtime.frameAt(store.runtime.totalLength);
    entry = {
      pos: nodes[nodes.length - 1].p.clone(),
      tan: endFrame.tan.clone(),
      up: endFrame.up.clone(),
    };
  }

  const res = templates[id](entry, params);
  if (res.nodes.length === 0) return false;

  if (nodes.length === 0) {
    const newIds = res.nodes.map((n) => n.id);
    store.commit((d) => {
      d.start.position.copy(res.nodes[0].p);
      d.track.nodes.push(...res.nodes);
      stampRelBanksOntoNodes(d.track.nodes, 0, res.rollKeys, res.length, d.track.closed);
    });
    store.setSelection(newIds);
    return true;
  }

  const newIds = res.nodes.slice(1).map((n) => n.id);
  store.commit((d) => {
    const last = d.track.nodes[d.track.nodes.length - 1];
    last.hOut.copy(res.nodes[0].hOut);
    const from = d.track.nodes.length;
    d.track.nodes.push(...res.nodes.slice(1));
    stampRelBanksOntoNodes(d.track.nodes, from, res.rollKeys, res.length, d.track.closed);
  });
  store.setSelection(newIds);
  return newIds.length > 0;
}

/**
 * Append an RCT-style grid piece at the free end. Heading is snapped to 45°
 * so successive pieces chain on the world grid. Open tracks only.
 */
export function appendRctPiece(store: AppStore, id: RctPieceId): boolean {
  if (store.doc.track.closed) return false;
  const piece = RCT_PIECE_BY_ID[id];
  if (!piece) return false;
  const nodes = store.doc.track.nodes;
  if (nodes.length === 0) return false;

  const runtime = store.runtime;
  const endFrame = runtime.frameAt(runtime.totalLength);
  const entry = rctEntryFrom(nodes[nodes.length - 1].p, endFrame.tan, endFrame.up);
  const res = piece.build(entry);

  store.commit((d) => {
    const last = d.track.nodes[d.track.nodes.length - 1];
    last.hOut.copy(res.nodes[0].hOut);
    if (last.mode === 'smooth') {
      const inLen = last.hIn.length() || last.hOut.length();
      last.hIn.copy(last.hOut).normalize().multiplyScalar(-inLen);
    }
    const from = d.track.nodes.length;
    d.track.nodes.push(...res.nodes.slice(1));
    stampRelBanksOntoNodes(d.track.nodes, from, res.rollKeys, res.length, d.track.closed);
  });
  return true;
}

/**
 * Place the first station node on the grid (empty track only). Facing is a
 * snapped horizontal heading; height is snapped to half-cells.
 */
export function placeRctStart(store: AppStore, worldPos: Vector3, facing: Vector3): boolean {
  if (store.doc.track.nodes.length > 0) return false;
  const p = snapToGrid(worldPos);
  p.y = snapHeight(Math.max(worldPos.y, 2.5));
  const dir = facing.clone();
  dir.y = 0;
  if (dir.lengthSq() < 1e-8) dir.set(1, 0, 0);
  const tan = snapHeading(dir);
  const handleLen = 4;
  store.commit((doc) => {
    doc.start.position.copy(p);
    doc.track.nodes.push(
      makeNode(p, tan.clone().multiplyScalar(-handleLen), tan.clone().multiplyScalar(handleLen)),
    );
  });
  return true;
}

export function rotateSelection(store: AppStore, ids: Set<string>, pivot: Vector3, axis: Vector3, angle: number): void {
  store.commit((doc) => {
    for (const n of doc.track.nodes) {
      if (!ids.has(n.id)) continue;
      rotateNode(n, pivot, axis, angle);
    }
  });
}

export function rotateNode(n: TrackNode, pivot: Vector3, axis: Vector3, angle: number): void {
  n.p.sub(pivot).applyAxisAngle(axis, angle).add(pivot);
  n.hIn.applyAxisAngle(axis, angle);
  n.hOut.applyAxisAngle(axis, angle);
}

/** Captured node state, so a slider rotation can be re-applied from the original each frame. */
export interface NodeSnapshot {
  id: string;
  p: Vector3;
  hIn: Vector3;
  hOut: Vector3;
}

/** The three rotation axes a slider set turns about. All unit length. */
export interface RotationAxes {
  yaw: Vector3; // heading — turns the run left/right
  pitch: Vector3; // nose up/down
  roll: Vector3; // along the track — twists the run
}

export interface RotationAngles {
  yaw: number; // degrees
  pitch: number;
  roll: number;
}

export function snapshotNodes(nodes: TrackNode[]): NodeSnapshot[] {
  return nodes.map((n) => ({ id: n.id, p: n.p.clone(), hIn: n.hIn.clone(), hOut: n.hOut.clone() }));
}

export function worldAxes(): RotationAxes {
  return { yaw: new Vector3(0, 1, 0), pitch: new Vector3(1, 0, 0), roll: new Vector3(0, 0, 1) };
}

/**
 * Track-local triad at a node: tangent from its handles (the Bézier derivative
 * there is 3·hOut), up = world up projected off the tangent, right = tan × up —
 * the same handedness as the render frames.
 */
export function nodeAxes(n: TrackNode): RotationAxes {
  const tan = n.hOut.clone();
  if (tan.lengthSq() < 1e-12) tan.copy(n.hIn).negate();
  if (tan.lengthSq() < 1e-12) tan.set(1, 0, 0);
  tan.normalize();

  const up = new Vector3(0, 1, 0).addScaledVector(tan, -tan.y);
  if (up.lengthSq() < 1e-6) up.set(0, 0, 1).addScaledVector(tan, -tan.z); // tangent is vertical
  up.normalize();

  return { yaw: up, pitch: new Vector3().crossVectors(tan, up).normalize(), roll: tan };
}

/**
 * Rotate `nodes` about `pivot` starting from their captured `base` state, so
 * dragging a slider back to 0 restores the original shape exactly. Rotations
 * compose twist → pitch → yaw about the fixed `axes`.
 *
 * With `progressive`, the angles ramp 0 → full across the snapshot order
 * instead of moving the selection rigidly: a straight run becomes a twist.
 */
export function rotateFromSnapshot(
  nodes: TrackNode[],
  base: NodeSnapshot[],
  pivot: Vector3,
  axes: RotationAxes,
  angles: RotationAngles,
  progressive = false,
): void {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const spread = base.length - 1;
  const rad = Math.PI / 180;
  base.forEach((b, k) => {
    const n = byId.get(b.id);
    if (!n) return;
    const f = progressive && spread > 0 ? k / spread : 1;
    n.p.copy(b.p);
    n.hIn.copy(b.hIn);
    n.hOut.copy(b.hOut);
    if (angles.roll) rotateNode(n, pivot, axes.roll, angles.roll * rad * f);
    if (angles.pitch) rotateNode(n, pivot, axes.pitch, angles.pitch * rad * f);
    if (angles.yaw) rotateNode(n, pivot, axes.yaw, angles.yaw * rad * f);
  });
}

/**
 * Twist banking over [s0, s1] by writing forcedBankDeg on nodes in range.
 * Re-derived from `baseBanks` every call, so a slider back at 0° restores
 * the original banks exactly.
 */
export function bankNodesFromSnapshot(
  nodes: TrackNode[],
  baseBanks: Map<string, number | undefined>,
  spanStartS: (i: number) => number,
  s0: number,
  s1: number,
  deltaDeg: number,
  totalLength: number,
  closed: boolean,
  ramp: boolean,
): void {
  for (const n of nodes) {
    const base = baseBanks.get(n.id);
    if (base === undefined) delete n.forcedBankDeg;
    else n.forcedBankDeg = base;
  }
  if (Math.abs(deltaDeg) < 1e-9) return;

  const baseKeys = rollKeysFromNodes(
    { nodes: nodes.map((n) => ({ forcedBankDeg: baseBanks.get(n.id) })) },
    spanStartS,
  );
  const range = s1 - s0;
  for (let i = 0; i < nodes.length; i++) {
    const s = spanStartS(i);
    if (!isFinite(s) || s < s0 - 1e-6 || s > s1 + 1e-6) continue;
    const t = range > 1e-6 ? (s - s0) / range : 1;
    const f = ramp ? t * t * (3 - 2 * t) : 1;
    const base = rollAt(baseKeys, s, totalLength, closed);
    nodes[i].forcedBankDeg = Math.round(base + deltaDeg * f);
  }
}

/**
 * @deprecated Prefer {@link bankNodesFromSnapshot}. Kept for tests that still
 * exercise free roll-key lists.
 */
export function bankFromSnapshot(
  baseKeys: RollKey[],
  s0: number,
  s1: number,
  deltaDeg: number,
  totalLength: number,
  closed: boolean,
  ramp: boolean,
  spacing = 4,
): RollKey[] {
  if (deltaDeg === 0) return sortRollKeys(baseKeys);
  const base = (s: number) => rollAt(baseKeys, s, totalLength, closed);
  const outside = baseKeys.filter((k) => k.s < s0 - 1e-6 || k.s > s1 + 1e-6);

  const range = s1 - s0;
  if (range < 1e-6) {
    return sortRollKeys([...outside, { s: s0, deg: base(s0) + deltaDeg, interp: 'smooth' }]);
  }

  const steps = Math.min(Math.max(Math.ceil(range / spacing), 2), 40);
  const keys: RollKey[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const s = s0 + range * t;
    const f = ramp ? t * t * (3 - 2 * t) : 1;
    keys.push({ s, deg: base(s) + deltaDeg * f, interp: i === 0 ? 'smooth' : 'linear' });
  }
  return sortRollKeys([...outside, ...keys]);
}

/** Stamp template-relative (RMF) roll samples onto newly appended nodes as gravity bank. */
export function stampRelBanksOntoNodes(
  nodes: TrackNode[],
  fromIndex: number,
  relKeys: { ds: number; deg: number; interp: 'smooth' | 'linear' }[],
  approxLength: number,
  closed = false,
): void {
  if (relKeys.length === 0 || fromIndex >= nodes.length || approxLength < 1e-6) return;
  const keys = sortRollKeys(relKeys.map((k) => ({ s: k.ds, deg: k.deg, interp: k.interp })));
  const count = nodes.length - fromIndex;
  const stamped: number[] = [];
  for (let i = 0; i < count; i++) {
    const s = ((i + 1) / count) * approxLength;
    nodes[fromIndex + i]!.forcedBankDeg = Math.round(rollAt(keys, s, approxLength, false));
    stamped.push(fromIndex + i);
  }
  convertNodeAuthoredBanksToGravity(new Centerline({ closed, nodes }), stamped);
}

/** Uniform scale of a node about `origin`. Handles are relative, so only their length scales. */
export function scaleNode(n: TrackNode, origin: Vector3, factor: number): void {
  n.p.sub(origin).multiplyScalar(factor).add(origin);
  n.hIn.multiplyScalar(factor);
  n.hOut.multiplyScalar(factor);
}

/**
 * Scale `nodes` about `origin` starting from their captured `base` state, so
 * dragging a slider back to 1 restores the original shape exactly.
 */
export function scaleFromSnapshot(
  nodes: TrackNode[],
  base: NodeSnapshot[],
  origin: Vector3,
  factor: number,
): void {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const b of base) {
    const n = byId.get(b.id);
    if (!n) continue;
    n.p.copy(b.p);
    n.hIn.copy(b.hIn);
    n.hOut.copy(b.hOut);
    if (factor !== 1) scaleNode(n, origin, factor);
  }
}

/** Scale arc-length metadata so bank keys and sections stay glued to the track. */
export function scaleArcLengths(keys: RollKey[], factor: number): RollKey[] {
  if (factor === 1) return keys.map((k) => ({ ...k }));
  return sortRollKeys(keys.map((k) => ({ ...k, s: k.s * factor })));
}

export function scaleSections(sections: Section[], factor: number): Section[] {
  if (factor === 1) return sections.map((s) => ({ ...s }));
  return sections.map((s) => ({ ...s, from: s.from * factor, to: s.to * factor }));
}

/** Scale a world-space point about `origin` (e.g. doc.start.position). */
export function scalePointAbout(p: Vector3, origin: Vector3, factor: number): void {
  p.sub(origin).multiplyScalar(factor).add(origin);
}

export function smoothSelection(store: AppStore): void {
  const idx = store.selectedIndices();
  store.commit((doc) => {
    if (idx.length >= 2) {
      smoothHandles(doc.track, idx[0], idx[idx.length - 1]);
    } else {
      smoothHandles(doc.track);
    }
  });
}

/** Auto-bank over the selected node range (or everywhere), from simulated speeds. */
export function autoBankSelection(store: AppStore, fraction = 0.9): void {
  const doc = store.doc;
  const runtime = store.runtime;
  const idx = store.selectedIndices();
  const cl = runtime.centerline;
  const s0 = idx.length >= 2 ? cl.spanStartS(idx[0]) : 0;
  const s1 = idx.length >= 2 ? cl.spanStartS(idx[idx.length - 1]) : runtime.totalLength;

  // speed profile from an energy march with the permanent min-speed floor
  const sim = new Simulator(runtime, doc.train, doc.settings);
  const station = doc.sections.find((s) => s.kind === 'station');
  const startS = station ? station.to : 0;
  const minV = Math.max(doc.settings.minSpeed, 2);
  const L = runtime.totalLength;
  const speeds = new Map<number, number>();
  let v2 = minV * minV;
  for (let d = 0; d < L; d += 1) {
    const s = cl.normalizeS(startS + d);
    const v = Math.sqrt(Math.max(v2, minV * minV));
    speeds.set(Math.round(s), v);
    v2 = Math.max(v2 + 2 * sim.accel(s, v) * 1, minV * minV);
  }
  const speedAt = (s: number) => speeds.get(Math.round(cl.normalizeS(s))) ?? 10;
  const lateralCurvAt = (s: number) => {
    const f = runtime.frameAt(s);
    const kvec = cl.curvatureVecAt(s);
    // signed toward the unbanked right axis (project out the roll)
    const flatRight = new Vector3().crossVectors(f.tan, new Vector3(0, 1, 0));
    if (flatRight.lengthSq() < 1e-6) return 0;
    flatRight.normalize();
    return kvec.dot(flatRight);
  };

  store.commit((d) => {
    const keys = autoBankKeys(s0, s1, speedAt, lateralCurvAt, d.settings.gravity, fraction, 6);
    for (let i = 0; i < d.track.nodes.length; i++) {
      const s = cl.spanStartS(i);
      if (!isFinite(s) || s < s0 - 0.5 || s > s1 + 0.5) continue;
      d.track.nodes[i].forcedBankDeg = Math.round(rollAt(keys, s, L, false));
    }
  });
}

export function autoSolveChecks(store: AppStore): TrackWarning[] {
  store.commit((doc) => {
    autoSolveTrackWarnings(doc.track);
  });
  return validateTrack(store.runtime.centerline, store.doc.rollKeys);
}

export function setClosed(store: AppStore, closed: boolean): void {
  if (store.doc.track.nodes.length < 3 && closed) return;
  store.commit((doc) => {
    doc.track.closed = closed;
  });
}

/** Node-index dirty range → span list for chunked mesh rebuilds. */
export function dirtySpansFor(store: AppStore, nodeIndices: number[]): number[] {
  const spans = new Set<number>();
  for (const i of nodeIndices) {
    for (const s of dirtySpansForNode(store.doc.track, i)) spans.add(s);
  }
  const n = spanCount(store.doc.track);
  return [...spans].filter((s) => s >= 0 && s < n);
}
